import { useEffect, useMemo, useState } from "react";
import { useGetList, useListContext, useTranslate } from "ra-core";
import {
  Archive,
  Check,
  ChevronDown,
  Link2,
  Megaphone,
  Search,
  Users,
  Zap,
} from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { ContactSource, Population, Tag, Temperature } from "../types";

// Sprint 7 T7.2.a — FilterBar horizontale (chips) conforme à
// design/atomic-crm/contacts-app.jsx (composant FilterBar) +
// contacts.css (.filter-bar / .fdrop / .fb-reset / .fb-count).
//
// 3 dropdowns implémentés (Population, Source, Température) qui matchent
// les champs DB existants. Le 4e de la maquette (Statut paiement)
// nécessite un join avec subscriptions — reporté Sprint 7.x.

type DropdownOption<V extends string> = {
  value: V | null;
  label: string;
  dot?: string;
};

type DropdownConfig<V extends string> = {
  id: string;
  filterKey: string;
  icon: React.ReactNode;
  label: string;
  options: ReadonlyArray<DropdownOption<V>>;
  // Si présent, override l'icône par un dot coloré quand l'option active
  // a un `dot` défini (utilisé pour Température).
  useDotWhenActive?: boolean;
};

const POPULATION_OPTIONS: ReadonlyArray<DropdownOption<Population>> = [
  { value: null, label: "Toutes" },
  { value: "plateforme", label: "Plateforme", dot: "bg-emerald-500" },
  { value: "prospection", label: "Prospection", dot: "bg-orange-500" },
];

const SOURCE_OPTIONS: ReadonlyArray<DropdownOption<ContactSource>> = [
  { value: null, label: "Toutes" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "instantly", label: "Instantly" },
  { value: "facebook", label: "Facebook / Bouche-à-oreille" },
  { value: "plateforme", label: "Plateforme" },
  { value: "telephone", label: "Téléphone" },
  { value: "autre", label: "Autre" },
];

const TEMPERATURE_OPTIONS: ReadonlyArray<DropdownOption<Temperature>> = [
  { value: null, label: "Toutes" },
  { value: "chaud", label: "Chaud", dot: "bg-red-500" },
  { value: "a_chauffer", label: "À chauffer", dot: "bg-orange-500" },
  { value: "froid", label: "Froid", dot: "bg-blue-500" },
  { value: "perdu", label: "Perdu", dot: "bg-muted-foreground" },
];

const DROPDOWNS: ReadonlyArray<DropdownConfig<string>> = [
  {
    id: "population",
    filterKey: "population",
    icon: <Users className="size-3.5" />,
    label: "Population",
    options: POPULATION_OPTIONS as ReadonlyArray<DropdownOption<string>>,
  },
  {
    id: "source",
    filterKey: "source",
    icon: <Link2 className="size-3.5" />,
    label: "Source",
    options: SOURCE_OPTIONS as ReadonlyArray<DropdownOption<string>>,
  },
  {
    id: "acquisition_source",
    filterKey: "acquisition_source",
    icon: <Link2 className="size-3.5" />,
    label: "Acquisition",
    options: [
      { value: null, label: "Toutes" },
      { value: "google", label: "Google" },
      { value: "linkedin", label: "LinkedIn" },
      { value: "direct", label: "Direct" },
    ] as ReadonlyArray<DropdownOption<string>>,
  },
  {
    id: "temperature",
    filterKey: "temperature",
    icon: <Zap className="size-3.5" />,
    label: "Température",
    options: TEMPERATURE_OPTIONS as ReadonlyArray<DropdownOption<string>>,
    useDotWhenActive: true,
  },
];

// Reprise 07/09/2026 — La campagne = les tags. Le filtre par tag n'existait
// que sur mobile (ContactListFilter), donc une campagne de 1 767 contacts était
// invisible depuis le desktop. Le dropdown est construit dynamiquement (liste
// des tags) et poussé à la fin de DROPDOWNS.
const CAMPAIGN_FILTER_KEY = "tags@cs";

const FILTER_KEYS = [...DROPDOWNS.map((d) => d.filterKey), CAMPAIGN_FILTER_KEY];

export const ContactFilterBar = () => {
  const translate = useTranslate();
  const { filterValues, setFilters, total, isPending } = useListContext();
  const [searchInput, setSearchInput] = useState<string>(
    () => (filterValues?.q as string) ?? "",
  );
  const { data: tags } = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const dropdowns = useMemo<ReadonlyArray<DropdownConfig<string>>>(
    () => [
      ...DROPDOWNS,
      {
        id: "campaign",
        filterKey: CAMPAIGN_FILTER_KEY,
        icon: <Megaphone className="size-3.5" />,
        label: "Campagne",
        options: [
          { value: null, label: "Toutes" },
          ...(tags ?? []).map((tag) => ({
            value: `{${tag.id}}`,
            label: tag.name,
          })),
        ],
      },
    ],
    [tags],
  );

  // Debounce search input → filterValues.q
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const current = (filterValues?.q as string) ?? "";
      if (searchInput === current) return;
      const next = { ...(filterValues ?? {}) };
      if (searchInput.trim()) {
        next.q = searchInput.trim();
      } else {
        delete next.q;
      }
      setFilters?.(next, []);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput, filterValues, setFilters]);

  const applyDropdown = (filterKey: string, value: string | null) => {
    const next = { ...(filterValues ?? {}) };
    if (value === null) {
      delete next[filterKey];
    } else {
      next[filterKey] = value;
    }
    setFilters?.(next, []);
  };

  const handleReset = () => {
    setSearchInput("");
    // Sprint 10.1 : reset conserve le filtre archivés (cohérent avec
    // filterDefaultValues de ContactList) — sauf si l'utilisateur a
    // explicitement coché "Inclure archivés".
    setFilters?.({ "archived_at@is": null }, []);
  };

  // Sprint 10.1 — toggle "Inclure archivés".
  const includeArchived = filterValues?.["archived_at@is"] === undefined;
  const toggleArchived = () => {
    const next = { ...(filterValues ?? {}) };
    if (includeArchived) {
      next["archived_at@is"] = null;
    } else {
      delete next["archived_at@is"];
    }
    setFilters?.(next, []);
  };

  // Filtres actifs = clés gérées par la barre, hors recherche libre.
  const hasActive = useMemo(
    () =>
      FILTER_KEYS.some(
        (k) => filterValues?.[k] !== undefined && filterValues?.[k] !== null,
      ),
    [filterValues],
  );

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 bg-card border border-border rounded-xl">
      {/* Search input */}
      <div className="flex items-center gap-2 h-8 w-full sm:w-[280px] shrink-0 px-2.5 bg-muted/50 border border-border rounded-md">
        <Search className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={translate("resources.contacts.filters.search", {
            _: "Rechercher un contact, une entreprise…",
          })}
          className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-muted-foreground"
          aria-label="Rechercher"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput("")}
            className="text-[11px] text-muted-foreground hover:text-foreground px-1"
            aria-label="Effacer la recherche"
          >
            ✕
          </button>
        )}
      </div>

      <div className="hidden sm:block w-px h-4 bg-border shrink-0" />

      {/* Dropdowns */}
      {dropdowns.map((d) => {
        const activeValue = filterValues?.[d.filterKey] as string | undefined;
        const activeOption = d.options.find((o) => o.value === activeValue);
        const isActive = !!activeValue;

        return (
          <Popover key={d.id}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-[13px] whitespace-nowrap shrink-0",
                  isActive
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900/40 dark:text-emerald-200"
                    : "bg-background border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                {d.useDotWhenActive && activeOption?.dot ? (
                  <span
                    className={cn("size-1.5 rounded-full", activeOption.dot)}
                  />
                ) : (
                  <span
                    className={cn(
                      "shrink-0",
                      isActive
                        ? "text-emerald-700 dark:text-emerald-300"
                        : "text-muted-foreground",
                    )}
                  >
                    {d.icon}
                  </span>
                )}
                {isActive && activeOption ? (
                  <>
                    <span className="text-muted-foreground font-normal">
                      {d.label} :
                    </span>
                    <span className="font-medium">{activeOption.label}</span>
                  </>
                ) : (
                  <span>{d.label}</span>
                )}
                <ChevronDown className="size-3 text-muted-foreground/70 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={4}
              className="p-1 w-[200px]"
            >
              {d.options.map((opt) => {
                const selected =
                  (opt.value === null && !activeValue) ||
                  opt.value === activeValue;
                return (
                  <button
                    key={opt.value ?? "_all"}
                    type="button"
                    onClick={() => applyDropdown(d.filterKey, opt.value)}
                    className={cn(
                      "w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] text-left",
                      "hover:bg-muted/60",
                      selected && "bg-muted/40",
                    )}
                  >
                    {opt.dot ? (
                      <span className={cn("size-1.5 rounded-full", opt.dot)} />
                    ) : (
                      <span className="size-1.5" />
                    )}
                    <span className="flex-1">{opt.label}</span>
                    {selected && (
                      <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                    )}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
        );
      })}

      <button
        type="button"
        onClick={toggleArchived}
        className={cn(
          "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-[13px] whitespace-nowrap shrink-0",
          includeArchived
            ? "bg-fabrik-orange-soft border-fabrik-orange text-fabrik-orange"
            : "bg-background border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground",
        )}
        title={
          includeArchived
            ? "Cliquer pour masquer les contacts archivés"
            : "Cliquer pour inclure les contacts archivés"
        }
      >
        <Archive className="size-3.5" />
        <span>{includeArchived ? "Archivés inclus" : "Archivés"}</span>
      </button>

      {hasActive && (
        <button
          type="button"
          onClick={handleReset}
          className="text-[12.5px] text-muted-foreground hover:text-foreground hover:underline px-1.5 shrink-0"
        >
          Réinitialiser
        </button>
      )}

      <span className="ml-auto text-[12.5px] text-muted-foreground tabular-nums shrink-0">
        {isPending
          ? "Chargement…"
          : `${total ?? 0} contact${(total ?? 0) > 1 ? "s" : ""}`}
      </span>
    </div>
  );
};
